import React from 'react';
import './Select.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Select label */
  label?: string;
  /** Error message */
  error?: string;
  /** Help text */
  helpText?: string;
  /** Full width select */
  fullWidth?: boolean;
  /** Density preset */
  density?: 'compact' | 'default' | 'comfortable';
  /** Options array */
  options: SelectOption[];
  /** Placeholder option */
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helpText,
      fullWidth = false,
      density = 'default',
      options,
      placeholder,
      required,
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${React.useId()}`;
    const errorId = error ? `${selectId}-error` : undefined;
    const helpTextId = helpText ? `${selectId}-help` : undefined;

    const wrapperClassNames = [
      'ui-select-wrapper',
      fullWidth && 'ui-select-wrapper--full-width',
      `ui-select-wrapper--${density}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const selectClassNames = [
      'ui-select',
      `ui-select--${density}`,
      error && 'ui-select--error',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassNames}>
        {label && (
          <label htmlFor={selectId} className="ui-select__label">
            {label}
            {required && <span className="ui-select__required" aria-label="required"> *</span>}
          </label>
        )}
        <div className="ui-select__container">
          <select
            ref={ref}
            id={selectId}
            className={selectClassNames}
            disabled={disabled}
            required={required}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={[errorId, helpTextId].filter(Boolean).join(' ') || undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <span className="ui-select__icon" aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        {error && (
          <span id={errorId} className="ui-select__error" role="alert">
            {error}
          </span>
        )}
        {helpText && !error && (
          <span id={helpTextId} className="ui-select__help-text">
            {helpText}
          </span>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
